import React, { useState, useEffect } from 'react';
import { orderAPI } from '../../services/api';
import './Planning.css';

function SplitOrderItemModal({ isOpen, onClose, order, line, onSplitComplete }) {
  const [numSplits, setNumSplits] = useState(1);
  const [splits, setSplits] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [autoAdjust, setAutoAdjust] = useState(true);

  const sizePerUnit = line?.size || 1;
  const originalQty = line?.qty || 1;
  const totalCubicMetres = sizePerUnit * originalQty;
  
  // Can only split if total cubic metres >= 2 (need at least 2 cubes to split into whole numbers)
  // Minimum 2 cubic metres total to allow splitting
  const canSplit = totalCubicMetres >= 2;

  // Initialize splits when modal opens or numSplits changes
  useEffect(() => {
    if (isOpen && totalCubicMetres > 0 && canSplit) {
      initializeSplits();
    }
  }, [isOpen, numSplits, canSplit]);

  const initializeSplits = () => {
    // Ensure each split gets at least 1 cubic metre, and we can't have more splits than cubic metres
    const maxSplits = Math.min(5, Math.floor(totalCubicMetres));
    const validNumSplits = Math.max(1, Math.min(maxSplits, numSplits));
    setNumSplits(validNumSplits);
    
    // Start with equal whole number distribution
    const baseSize = Math.floor(totalCubicMetres / validNumSplits);
    const remainder = totalCubicMetres % validNumSplits;
    
    const initialSplits = Array.from({ length: validNumSplits }, (_, i) => {
      return i < remainder ? baseSize + 1 : baseSize;
    });
    
    setSplits(initialSplits);
  };

  const handleSplitCountChange = (count) => {
    // Ensure we don't try to split into more pieces than cubic metres available
    const maxSplits = Math.min(5, Math.floor(totalCubicMetres));
    const validCount = Math.min(count, maxSplits);
    setNumSplits(validCount);
  };

  const handleSliderChange = (index, newValue) => {
    const value = Math.round(Number(newValue)); // Round to whole numbers
    const minValue = 1;
    const maxValue = Math.min(7, totalCubicMetres - (numSplits - 1) * minValue); // Leave at least 1 for others, max 7 per piece
    const clampedValue = Math.max(minValue, Math.min(maxValue, value));

    const newSplits = [...splits];
    const oldValue = newSplits[index];
    const diff = clampedValue - oldValue;
    
    newSplits[index] = clampedValue;

    // Only auto-adjust if the toggle is on
    if (autoAdjust && diff !== 0) {
      // Only adjust pieces AFTER the current index (not before)
      // So if you adjust piece 1, only pieces 2, 3, etc. are affected
      
      // Calculate the total available in later pieces (current total minus minimum 1 each)
      const laterIndices = newSplits.map((_, i) => i).filter(i => i > index);
      
      if (laterIndices.length > 0) {
        const currentLaterTotal = laterIndices.reduce((sum, i) => sum + newSplits[i], 0);
        const minimumLaterTotal = laterIndices.length * minValue;
        const availableLater = currentLaterTotal - minimumLaterTotal;
        
        // If we want to INCREASE this piece (diff > 0), we can only do so if later pieces have enough to give
        // The amount we can increase is limited by how much later pieces can spare (availableLater)
        if (diff > 0 && diff > availableLater) {
          // Can't increase as much as requested - limit to available capacity
          clampedValue = oldValue + availableLater;
          newSplits[index] = Math.max(minValue, clampedValue);
        }
        
        // Recalculate diff after clamping
        const actualDiff = newSplits[index] - oldValue;
        
        // Distribute the difference among later splits only
        let remainingDiff = actualDiff; // If diff is positive, need to decrease later pieces; if negative, increase them
        
        for (const laterIdx of laterIndices) {
          if (remainingDiff === 0) break;
          
          if (remainingDiff > 0) {
            // We increased current slider, need to decrease later splits
            const canDecrease = newSplits[laterIdx] - 1; // Can only decrease to minimum of 1
            const decrease = Math.min(remainingDiff, canDecrease);
            newSplits[laterIdx] -= decrease;
            remainingDiff -= decrease;
          } else {
            // We decreased current slider, need to increase later splits
            const canIncrease = maxValue - newSplits[laterIdx]; // Max is the calculated maxValue
            const increase = Math.min(Math.abs(remainingDiff), canIncrease);
            newSplits[laterIdx] += increase;
            remainingDiff += increase;
          }
        }
        
        // If still not balanced (edge cases), adjust the last piece
        if (remainingDiff !== 0 && laterIndices.length > 0) {
          const lastIdx = laterIndices[laterIndices.length - 1];
          const adjustment = remainingDiff > 0 
            ? Math.min(remainingDiff, newSplits[lastIdx] - 1) 
            : Math.min(Math.abs(remainingDiff), maxValue - newSplits[lastIdx]);
          newSplits[lastIdx] += remainingDiff > 0 ? -adjustment : adjustment;
        }
      } else {
        // No later pieces to adjust - the user is adjusting the last piece
        // In this case, we just let it be (no auto-adjustment possible)
      }
    }

    setSplits(newSplits.map(v => Math.round(v)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Call API to split the order line - each split gets qty=1 and size = whole number cubic metres
      await orderAPI.splitOrderLine(line.id, {
        splits: splits.map(cubicMetres => ({
          qty: 1, // Each split piece is qty 1
          size: cubicMetres
        }))
      });

      onSplitComplete();
      onClose();
    } catch (err) {
      console.error('Error splitting order line:', err);
      setError(err.response?.data?.message || 'Failed to split order item');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Show message if splitting is not possible
  if (!canSplit) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-split" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Cannot Split Order Item</h2>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="split-order-info">
              <p><strong>{order?.customer?.fullname || 'Unknown Customer'}</strong></p>
              <p>{line?.product?.code || 'Product'} × {line?.qty}</p>
              <p className="total-cubic">
                Total: <strong>{totalCubicMetres} cubic metre{totalCubicMetres !== 1 ? 's' : ''}</strong>
              </p>
            </div>
            <p className="split-not-possible">
              This item cannot be split into whole number cubes. Only items with more than 1 cubic metre can be split.
            </p>
          </div>
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-split" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Split Order Item</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="split-order-info">
            <p><strong>{order?.customer?.fullname || 'Unknown Customer'}</strong></p>
            <p>{line?.product?.code || 'Product'} × {originalQty}</p>
            <p className="total-cubic">
              Total: <strong>{totalCubicMetres} cubic metre{totalCubicMetres !== 1 ? 's' : ''}</strong>
            </p>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="split-count-selector">
            <label>Number of Pieces (Splits)</label>
            <div className="split-count-buttons">
              {[1, 2, 3, 4, 5].map(count => {
                // Disable if count exceeds total cubic metres or count > 5
                const disabled = count > totalCubicMetres || count > 5;
                return (
                  <button
                    key={count}
                    type="button"
                    className={`split-count-btn ${numSplits === count ? 'selected' : ''}`}
                    onClick={() => handleSplitCountChange(count)}
                    disabled={disabled || saving}
                  >
                    {count}
                  </button>
                );
              })}
            </div>
            <span className="hint">Maximum {Math.min(5, totalCubicMetres)} splits for {totalCubicMetres} cubic metres</span>
          </div>

          <div className="split-auto-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={autoAdjust}
                onChange={(e) => setAutoAdjust(e.target.checked)}
                className="toggle-checkbox"
              />
              <span className="toggle-text">Auto-adjust other pieces when sliding</span>
            </label>
            <span className="hint">When enabled, adjusting one piece will automatically adjust subsequent pieces</span>
          </div>

          <div className="splits-container">
            {splits.map((value, index) => (
              <div key={index} className="split-row">
                <span className="split-label">Piece {index + 1}</span>
                <input
                  type="range"
                  min="1"
                  max={Math.min(7, totalCubicMetres - (numSplits - 1))}
                  step="1"
                  value={value}
                  onChange={(e) => handleSliderChange(index, e.target.value)}
                  className="split-slider"
                  disabled={saving}
                />
                <span className="split-value">{value} m³</span>
              </div>
            ))}
          </div>

          <div className="splits-summary">
            {(() => {
              const currentTotal = splits.reduce((sum, val) => sum + val, 0);
              const isBalanced = currentTotal === totalCubicMetres;
              return (
                <>
                  <span className={isBalanced ? '' : 'summary-error'}>
                    Total: {currentTotal} / {totalCubicMetres} m³
                  </span>
                  {!isBalanced && (
                    <span className="balance-error">
                      ⚠️ Pieces don't add up correctly
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || splits.reduce((sum, val) => sum + val, 0) !== totalCubicMetres}
          >
            {saving ? 'Splitting...' : 'Split Item'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SplitOrderItemModal;